import { CurrencyPipe, DatePipe, TitleCasePipe } from '@angular/common';
import { formatSystemDate } from 'src/app/shared/utils/system-date.util';
import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subscription, timer } from 'rxjs';
import { switchMap, takeUntil, takeWhile } from 'rxjs/operators';

import { DialogTypeEnum, IDialogConfirm } from '../../../models/dialog';
import { IFilterBatchSearch, IFilterContracts, IFilterOptions } from '../../../models/filters';
import { HeaderTitle, IGenericResult, IHeaderItem, IHeaderSummary, ILot, IPagination, ISearchSummary, ISendBatchEmail, ITransferAutomaticBatchExecution, ITransferBatchMassCreateResult } from '../../../models/generic';
import { IFieldOrder } from '../../../models/manual-batch';
import { ApiVerificationService } from '../../../services/api/api-verification.service';
import { ApiLotsService } from '../../../services/api/api-lots.service';
import { ApiDialogService } from '../../../services/api/api-cdialog.service';
import { DataService } from '../../../services/data.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { IMassCreateExecutionInfo, ModalTransferBatchMassCreateResultComponent } from '../modal-transfer-batch-mass-create-result/modal-transfer-batch-mass-create-result.component';

@Component({
  selector: 'app-listagem-lotes-repasse',
  templateUrl: './listagem-lotes-repasse.component.html',
  styleUrls: ['../listagem-lotes/listagem-lotes.component.scss']
})
export class ListagemLotesRepasseComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('headerFix') headerFixRef?: ElementRef<HTMLDivElement>;
  @ViewChild('repasseHeaderRegion') headerRegionRef?: ElementRef<HTMLDivElement>;
  @ViewChild('listBody') listBodyRef?: ElementRef<HTMLDivElement>;
  
  @Input() headerItems: IHeaderItem[] = [];

  title: HeaderTitle = 'Repasse de Comissão';
  lotsList: ILot[] = [];
  summaryList: ISearchSummary = {} as ISearchSummary;

  checkedBatches: string[] = [];
  exceptionsBatches: string[] = [];
  queryField = new FormControl<string>('', { nonNullable: true });
  batchDateFrom = new FormControl<string>('', { nonNullable: true });
  batchDateTo = new FormControl<string>('', { nonNullable: true });
  contractInclusionDateFrom = new FormControl<string>('', { nonNullable: true });
  contractInclusionDateTo = new FormControl<string>('', { nonNullable: true });
  search!: string;
  filters!: IFilterContracts;
  showFilters: boolean = false;
  isFiltersExpanded: boolean = true;
  openDropdown: 'transferStatuses' | 'orderStatuses' | 'paymentStatuses' | null = null;
  firsLotsList: boolean = false;
  checkAll: boolean = false;
  fieldsOrder: IFieldOrder[] = [];
  transferStatusOptions: string[] = [];
  orderStatusOptions: string[] = [];
  paymentStatusOptions: string[] = ['Pendente', 'Agendado', 'Pago'];
  selectedTransferStatuses: string[] = [];
  selectedOrderStatuses: string[] = [];
  selectedPaymentStatuses: string[] = [];

  buildFiltersSubscription!: Subscription;
  isHeaderPinned: boolean = false;
  headerHeight: number = 0;
  headerLeft: number = 0;
  headerWidth: number = 0;
  private readonly enablePinnedHeader: boolean = false;

  count = 0;
  take = 50;
  skip = 0;
  firstLoad: boolean = true;
  isLoading: boolean = false;
  isLoadingAutomatic: boolean = false;
  integratedPaymentStatuses: string[] = ['liberado', 'liberado manual', 'liberado parcialmente'];
  private automaticExecutionSubscription?: Subscription;

  get canSendTransferEmail(): boolean {
    return this.lotsList.length > 0 && this.hasSelectedTransferBatchesForEmail(false);
  }

  get canSendTransferPaymentEmail(): boolean {
    return this.lotsList.length > 0 && this.hasSelectedTransferBatchesForEmail(true);
  }

  get subTotalPageAmount(): number {
    return this.lotsList.reduce((sum: number, lot: ILot) => sum + (lot.AmountTotal || 0), 0);
  }

  filter: IFilterOptions = {
    toggleFilter: () => (this.showFilters = !this.showFilters)
  };

  constructor(
    private _dataService: DataService,
    private _activatedRoute: ActivatedRoute,
    private _apiLotsService: ApiLotsService,
    private _verificationService: ApiVerificationService,
    private _router: Router,
    private _dialogService: ApiDialogService,
    private _snackbarService: SnackbarService,
    private _datePipe: DatePipe,
    private _currencyPipe: CurrencyPipe,
    private _titleCasePipe: TitleCasePipe,
  ) { }

  ngOnInit(): void {
    this.firsLotsList = true;
    this.loadTransferFilterOptions();
    this.getStorageFilter();
    this.buildFilters();
    this.buildColumns();
    this.resumeAutomaticBatchExecutionIfInProgress();
  }

  // O job automatico roda em background (Hangfire) independente da tela estar
  // aberta - se a pessoa recarregar a pagina (ou abrir em outra aba) enquanto
  // uma execucao ainda esta rodando, reabre o modal de acompanhamento sozinho
  // em vez de deixar a execucao "invisivel" ate ela terminar. Execuções já
  // concluídas não reabrem sozinhas aqui (isso seria um modal surpresa a cada
  // reload) - para isso existe o "Ver última execução" (viewLatestAutomaticBatchExecution).
  private resumeAutomaticBatchExecutionIfInProgress(): void {
    this._apiLotsService.getLatestAutomaticTransferBatchExecution().subscribe({
      next: (response) => {
        const execution = response?.Data;

        if (execution && (execution.Status === 'Queued' || execution.Status === 'Running')) {
          this.openAutomaticBatchExecutionDialog(execution);
        }
      }
    });
  }

  // Botão "Ver última execução": o resultado fica persistido mesmo que a
  // pessoa tenha fechado o modal antes de terminar (ou saído da tela) -
  // sem isso, um clique novo em "Gerar lote automático" não acharia mais
  // nenhuma execução "em andamento" e simplesmente iniciaria outra do zero,
  // escondendo o relatório da rodada anterior.
  viewLatestAutomaticBatchExecution(): void {
    this._apiLotsService.getLatestAutomaticTransferBatchExecution().subscribe({
      next: (response) => {
        const execution = response?.Data;

        if (!execution) {
          this._snackbarService.openSnackBar('Nenhuma execução de loteamento automático encontrada ainda.', 'warning');
          return;
        }

        this.openAutomaticBatchExecutionDialog(execution);
      },
      error: () => {
        this._snackbarService.openSnackBar('Não foi possível consultar a última execução.', 'danger');
      }
    });
  }

  private openAutomaticBatchExecutionDialog(execution: ITransferAutomaticBatchExecution): void {
    const dialogRef = this._dialogService.openTransferBatchMassCreateResultDialog(null);
    const executionInfo = this.toExecutionInfo(execution);

    if (execution.Status === 'Completed') {
      dialogRef.componentInstance.setResult(execution.Result!, executionInfo);
    } else if (execution.Status === 'Failed') {
      dialogRef.componentInstance.setError(execution.ErrorMessage, executionInfo);
    } else {
      this.pollAutomaticBatchExecution(execution.ExecutionId, dialogRef);
    }
  }

  private toExecutionInfo(execution: ITransferAutomaticBatchExecution): IMassCreateExecutionInfo {
    return {
      ExecutionId: execution.ExecutionId,
      StartedAt: execution.StartedAt,
      FinishedAt: execution.FinishedAt,
      TriggeredByUsername: execution.TriggeredByUsername
    };
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.updateHeaderPin());
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.updateHeaderPin();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateHeaderPin();
  }

  ngOnDestroy(): void {
    if (this.buildFiltersSubscription)
      this.buildFiltersSubscription.unsubscribe();

    this.automaticExecutionSubscription?.unsubscribe();
  }

  buildColumns(): void {
    this.headerItems = [
      { label: 'NF', flexSize: 1 },
      { label: 'Lote', flexSize: 4 },
      { label: 'Banco', flexSize: 4 },
      { label: 'Parceiro', flexSize: 5, name: 'Partner', isOrder: true, orderValue: 0 },
      { label: 'Status do Pagamento', flexSize: 4 },
      { label: 'Data do Lote', flexSize: 4, name: 'CreateDate', isOrder: true, orderValue: 0 },
      { label: 'Data de envio e-mail', flexSize: 4, name: 'EmailTransferDate', isOrder: true, orderValue: 0 },
      { label: 'Data e-mail pgto', flexSize: 4, name: 'EmailEstimatedPaymentDate', isOrder: true, orderValue: 0 },
      { label: 'Valor do Repasse', flexSize: 4, name: 'AmountTotal', isOrder: true, orderValue: 0 },
      { label: 'Qt. de Contratos', flexSize: 4, name: 'NumberOfContracts', isOrder: true, orderValue: 0 },
      { label: 'Status do Lote', flexSize: 4, name: 'StatusOfOnlending', isOrder: true, orderValue: 0 },
      { label: '', flexSize: 1 }
    ];
  }

  toggleFilter(): void {
    this.filter.toggleFilter();
  }

  toggleFiltersVisibility(): void {
    this.isFiltersExpanded = !this.isFiltersExpanded;
  }

  toggleDropdown(event: Event, dropdown: 'transferStatuses' | 'orderStatuses' | 'paymentStatuses'): void {
    event.stopPropagation();
    this.openDropdown = this.openDropdown === dropdown ? null : dropdown;
  }

  closeDropdown(): void {
    this.openDropdown = null;
  }

  toggleTransferStatus(label: string, checked: boolean): void {
    this.selectedTransferStatuses = this.toggleSelection(this.selectedTransferStatuses, label, checked);
  }

  toggleOrderStatus(label: string, checked: boolean): void {
    this.selectedOrderStatuses = this.toggleSelection(this.selectedOrderStatuses, label, checked);
  }

  togglePaymentStatus(label: string, checked: boolean): void {
    this.selectedPaymentStatuses = this.toggleSelection(this.selectedPaymentStatuses, label, checked);
  }

  isTransferStatusSelected(label: string): boolean {
    return this.selectedTransferStatuses.includes(label.toLowerCase());
  }

  isOrderStatusSelected(label: string): boolean {
    return this.selectedOrderStatuses.includes(label.toLowerCase());
  }

  isPaymentStatusSelected(label: string): boolean {
    return this.selectedPaymentStatuses.includes(label.toLowerCase());
  }

  getTransferStatusTriggerText(): string {
    return this.getTriggerText(this.selectedTransferStatuses, this.transferStatusOptions.map((value) => ({ value, label: value })), 'Status do lote');
  }

  getOrderStatusTriggerText(): string {
    return this.getTriggerText(this.selectedOrderStatuses, this.orderStatusOptions.map((value) => ({ value, label: value })), 'Status do OMIE');
  }

  getPaymentStatusTriggerText(): string {
    return this.getTriggerText(this.selectedPaymentStatuses, this.paymentStatusOptions.map((value) => ({ value, label: value })), 'Status Pagamento');
  }

  applyRepasseFilters(): void {
    const formattedText = this.queryField.value
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '');

    this.search = formattedText;
    this.skip = 0;
    this.count = 0;

    this.filters = {
      ...this.filters,
      StatusOfOnlending: [...this.selectedTransferStatuses],
      OrderStatus: [...this.selectedOrderStatuses],
      PaymentStatus: [...this.selectedPaymentStatuses]
    };

    this._dataService.applyFilters(this.filters);

    const bag = {
      dataIni: this.batchDateFrom.value,
      dataFim: this.batchDateTo.value,
      dataIniAlteracao: this.contractInclusionDateFrom.value,
      dataFimAlteracao: this.contractInclusionDateTo.value,
      filtros: this.filter
    };

    localStorage.setItem('bag_t', JSON.stringify(bag));
    this.closeDropdown();
    this.showFilters = false;
  }

  clearRepasseFilters(): void {
    this.queryField.setValue('');
    this.batchDateFrom.setValue('');
    this.batchDateTo.setValue('');
    this.contractInclusionDateFrom.setValue('');
    this.contractInclusionDateTo.setValue('');
    this.search = '';
    this.showFilters = false;
    this.selectedTransferStatuses = [];
    this.selectedOrderStatuses = [];
    this.selectedPaymentStatuses = [];
    this.closeDropdown();

    localStorage.removeItem('bag_t');
    localStorage.removeItem('bag_filter');

    this._dataService.applyFilters(this.getDefaultFilters());
  }

  private getBatches(pushLots: boolean = false, pagination?: IPagination): void {
    let searchData = {
      StatusOfOnlending: this.filters.StatusOfOnlending,
      OrderStatus: this.filters.OrderStatus,
      PaymentStatus: this.filters.PaymentStatus,
      BatchStatus: this.filters.BatchStatus,
      Text: this.search,
      Skip: this.skip,
      Take: this.take,
      BatchDateFrom: this.batchDateFrom.value,
      IsLegacy: this.filters.IsLegacy,
      BatchDateTo: this.batchDateTo.value,
      LastContractInclusionDateFrom: this.contractInclusionDateFrom.value,
      LastContractInclusionDateTo: this.contractInclusionDateTo.value,
      FieldsOrder: [...this.fieldsOrder]
    } as IFilterBatchSearch;

    if (pagination) {
      searchData = { ...searchData, ...pagination };
    }

    if (!pushLots) {
      this.isLoading = true;
    }

    this._apiLotsService.getLotsTransfer(searchData).subscribe({
      next: (res) => {
        this.firsLotsList = false;
        this.count = res.Count;
        this.firstLoad = false;
        this.isLoading = false;

        if (pushLots)
          this.lotsList.push(...res.Data);
        else
          this.lotsList = res.Data;

        let summary: IHeaderSummary[] = [];

        if (res.Summary != null) {
          this.summaryList = res.Summary;
          summary = [
            { label: 'Lotes', value: res.Summary.TotalBatchs },
            { label: 'Contratos', value: res.Summary.TotalContracts },
            { label: 'Total de', value: this._currencyPipe.transform(res.Summary.BatchAmountSum, 'BRL') },
          ];
        }
        this._dataService.setHeaderSummary(summary);
        queueMicrotask(() => this.updateHeaderPin());
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  buildFilters(): void {
    this.buildFiltersSubscription = this._dataService.listFilters.subscribe(
      (filters) => {
        this.filters = filters;
        this.selectedTransferStatuses = (filters.StatusOfOnlending ?? []).map((status) => status.toLowerCase());
        this.selectedOrderStatuses = (filters.OrderStatus ?? []).map((status) => status.toLowerCase());
        this.selectedPaymentStatuses = (filters.PaymentStatus ?? []).map((status) => status.toLowerCase());

        if (this.firstLoad) {
          const storageFilter = localStorage.getItem('bag_filter');

          if (storageFilter != null)
            this.filters = JSON.parse(storageFilter);
          else
            this.filters = this.getDefaultFilters();
        }
        this.resetPagination();
        this.getBatches();
      }
    );
  }

  searchField(text: string): void {
    let formattedString = text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '');

    this.search = formattedString;
    this.resetPagination();
    this.getBatches();
  }

  searchDateField(): void {
    this.skip = 0;
    this.count = 0;

    const bag = {
      dataIni: this.batchDateFrom.value,
      dataFim: this.batchDateTo.value,
      dataIniAlteracao: this.contractInclusionDateFrom.value,
      dataFimAlteracao: this.contractInclusionDateTo.value,
      filtros: this.filter
    };

    localStorage.setItem('bag_t', JSON.stringify(bag));
    this.getBatches();
  }

  exportToClipboardTransfer(): void {
    const searchData = {
      StatusOfOnlending: this.filters.StatusOfOnlending,
      OrderStatus: this.filters.OrderStatus,
      PaymentStatus: this.filters.PaymentStatus,
      BatchStatus: this.filters.BatchStatus,
      Text: this.search,
      Skip: 0,
      Take: 100000,
      BatchDateFrom: this.batchDateFrom.value,
      IsLegacy: this.filters.IsLegacy,
      BatchDateTo: this.batchDateTo.value,
      LastContractInclusionDateFrom: this.contractInclusionDateFrom.value,
      LastContractInclusionDateTo: this.contractInclusionDateTo.value,
      FieldsOrder: [...this.fieldsOrder]
    } as IFilterBatchSearch;

    this._apiLotsService.getLotsTransfer(searchData).subscribe({
      next: (res: IGenericResult<ILot[]>) => {
        let content: string = 'Status\tLote\tBanco\tParceiro\tStatus do Pagamento\tData do Lote\tData e-mail pgto\tValor do Repasse\tQt. de Contratos\t Status do Lote\r\n';

        if (res.Data.length > 0) {
          res.Data.forEach((lote: ILot) => {
            let status = lote.Active! ? 'Ativo' : 'Cancelado';
            let statusPagamento = (!!lote.PaymentDate && 'Pago')
              || (lote.OmiePaymentStatus?.toLowerCase() === 'agendado' && 'Agendado')
              || 'Pendente';
            let statusLote = (lote.IntegrationError && 'Erro') || lote.BatchStatus || lote.StatusOfOnlending;
            content += `${status}\t ${lote.BatchNumber}\t${this._titleCasePipe.transform(lote.Bank)}\t${lote.Partner}\t${statusPagamento}\t${formatSystemDate(lote.CreateDate, 'dd/MM/yyyy')}\t${this._datePipe.transform(lote.EmailEstimatedPaymentDate, 'dd/MM/yyyy') || ''}\t${this._currencyPipe.transform(lote.AmountTotal, 'BRL')}\t${lote.NumberOfContracts}\t${statusLote}\r\n`;
          });
        }

        navigator.clipboard.writeText(content).then(() => {
          this._snackbarService.openSnackBar('Copiado para área de transferência!', 'success');
        });
      }
    });
  }

  handlePagination(pagination: IPagination): void {
    this.take = pagination.Take;
    this.skip = pagination.Skip;
    this.getBatches(true);
  }

  private getDefaultFilters(): IFilterContracts {
    return {
      Status: [],
      Stages: [],
      StatusOfOnlending: [],
      OrderStatus: [],
      PaymentStatus: [],
      BatchStatuses: [],
      BatchStatus: true,
      IsLegacy: false,
      IsZeroRate: false,
      WithoutBatch: false,
    };
  }

  private loadTransferFilterOptions(): void {
    this._verificationService.getTransferFilterStatus('transfer').subscribe({
      next: (response) => {
        const transferStatuses: Array<{ Status?: string; Description?: string }> = [
          ...(response.Data.BatchStatus ?? [])
        ];

        if (!transferStatuses.some((el) => el.Description?.toLowerCase() === 'em andamento')) {
          transferStatuses.push({ Status: 'Em Andamento', Description: 'Em Andamento' });
        }

        this.transferStatusOptions = transferStatuses
          .map((status: { Description?: string }) => status.Description ?? '')
          .filter((value: string, index: number, self: string[]) =>
            self.findIndex((item: string) => item.toLowerCase() === value.toLowerCase()) === index);

        this.orderStatusOptions = response.Data.OrderStatus
          .map((status: { Description?: string }) => status.Description ?? '')
          .filter((value: string, index: number, self: string[]) =>
            self.findIndex((item: string) => item.toLowerCase() === value.toLowerCase()) === index);
      }
    });
  }

  private toggleSelection(current: string[], value: string, checked: boolean): string[] {
    const normalizedValue = value.toLowerCase();

    if (checked && !current.includes(normalizedValue)) {
      return [...current, normalizedValue];
    }

    if (!checked) {
      return current.filter((item) => item !== normalizedValue);
    }

    return current;
  }

  private getTriggerText(
    selectedValues: string[],
    options: Array<{ value: string; label: string }>,
    fallback: string
  ): string {
    if (selectedValues.length === 0) {
      return fallback;
    }

    if (selectedValues.length === 1) {
      const selectedValue = selectedValues[0];
      const found = options.find((option) => option.value.toLowerCase() === selectedValue);
      return found?.label || fallback;
    }

    return `${selectedValues.length} selecionados`;
  }

  insertManualBatch(): void {
    this._dialogService.openManualContractList({}).then((response) => {
      if (response) {
        this._snackbarService.openSnackBar('Novo lote criado com sucesso!', 'success');
        this._router.navigate(['detalhes-lote-manual-v2', response.BatchId], {
          relativeTo: this._activatedRoute
        });
      }
    });
  }

  changeInvoiceHeaderCheckbox(): void {
    this.checkAll = !this.checkAll;
    this.checkedBatches = [];
    this.exceptionsBatches = [];

    if (this.checkAll) {
      this.lotsList
        .filter(lote => !this.disabledCardFlag(lote))
        .forEach(lote => this.handleCheckedBatches(lote.BatchId.toString()));
    }
  }

  checkCardFlag(batch: ILot): boolean {
    if (this.checkAll) {
      if (this.disabledCardFlag(batch)) {
        return false;
      }
      return !this.checkExceptionBatchId(batch.BatchId.toString());
    }
    return this.checkIncludedBatchId(batch.BatchId.toString());
  }

  disabledCardFlag(batch: ILot): boolean {
    return false;
  }

  checkIncludedBatchId(batchId: string): boolean {
    return this.checkedBatches.indexOf(batchId) >= 0;
  }

  checkExceptionBatchId(batchId: string): boolean {
    return this.exceptionsBatches.indexOf(batchId) >= 0;
  }

  changeInvoiceCardCheckbox(batchId: string): void {
    this.handleCheckedBatches(batchId);
  }

  handleCheckedBatches(batchId: string): void {
    let index = this.checkedBatches.indexOf(batchId);

    if (index >= 0) {
      this.checkedBatches.splice(index, 1);
    } else {
      this.checkedBatches.push(batchId);
    }
  }

  handleProcessAutomaticTransferBatches(): void {
    const monthYear = this._datePipe.transform(new Date(), 'MM/yyyy') || 'mês vigente';

    const dialogData: IDialogConfirm = {
      type: DialogTypeEnum.Optional,
      appearance: 'modern',
      template: {
        boldTitle: 'Confirmar geração automática',
        title: 'Deseja iniciar a geração automática de lotes de repasse?',
        description: `Somente contratos do mês de faturamento ${monthYear} serão considerados. A rotina irá buscar lotes em aberto nesse período para incluir contratos elegíveis neles quando possível.`,
        confirm: 'Confirmar',
        dismiss: 'Cancelar'
      }
    };

    this._dialogService.openDialog(dialogData).then((confirmResponse) => {
      if (confirmResponse) {
        this.createAutomaticTransferBatches();
      }
    });
  }

  handleSendInvoiceEmail(payment: boolean): void {
    if (!this.hasSelectedTransferBatchesForEmail(payment)) {
      const message = payment
        ? 'Selecione lotes com status Liberado, Liberado Manual ou Liberado Parcialmente.'
        : 'Selecione ao menos um lote apto para Enviar e-mail.';

      this._snackbarService.openSnackBar(message, 'warning');
      return;
    }

    if (this.checkAll || this.checkedBatches.length > 0) {
      const dialogData: IDialogConfirm = {
        type: DialogTypeEnum.Optional,
        appearance: 'modern',
        template: {
          boldTitle: 'Envio de e-mail',
          title: 'O disparo de e-mail será realizado para os lotes selecionados.',
          confirm: 'Confirmar',
          dismiss: 'Cancelar'
        }
      };

      this._dialogService.openDialog(dialogData).then((response) => {
        if (response) {
          this.sendEmail(payment);
        }
      });
    }
  }

  sendEmail(payment: boolean): void {
    const batches: ISendBatchEmail = {
      BatchIds: this.getSelectedTransferBatchIdsByEmailType(payment),
      ExceptionsBatchIds: [],
      SendAll: false,
      Filter: {
        BatchStatus: true,
        BatchStatuses: this.filters.BatchStatuses ?? [],
        Text: this.search ?? '',
        Skip: 0,
        Take: 100,
        BatchDateFrom: this.batchDateFrom.value,
        BatchDateTo: this.batchDateTo.value,
        IsLegacy: this.filters.IsLegacy,
        IsZeroRate: this.filters.IsZeroRate,
        WithoutBatch: this.filters.WithoutBatch,
      }
    };

    if (batches.BatchIds.length === 0) {
      const message = payment
        ? 'Nenhum lote selecionado está apto para envio de e-mail de pagamento.'
        : 'Nenhum lote selecionado está apto para envio de e-mail.';

      this._snackbarService.openSnackBar(message, 'warning');
      return;
    }

    if (payment)
      this.sendTransferEstimatedPaymentBatchEmail(batches);
    else
      this.sendTransferBatchEmail(batches);
  }

  hasSelectedTransferBatchesForEmail(payment: boolean): boolean {
    return this.getSelectedTransferBatchIdsByEmailType(payment).length > 0;
  }

  private getSelectedTransferBatchIdsByEmailType(payment: boolean): string[] {
    return this.lotsList
      .filter((lot) => this.checkedBatches.includes(lot.BatchId.toString()))
      .filter((lot) => !this.disabledCardFlag(lot))
      .filter((lot) => payment ? this.isIntegratedStatus(lot) : !this.isIntegratedStatus(lot))
      .map((lot) => lot.BatchId.toString());
  }

  private isIntegratedStatus(lot: ILot): boolean {
    const status = (lot.BatchStatus || lot.StatusOfOnlending || '').toLowerCase().trim();
    return this.integratedPaymentStatuses.includes(status);
  }

  private updateHeaderPin(): void {
    if (!this.enablePinnedHeader) {
      this.isHeaderPinned = false;
      this.headerHeight = 0;
      this.headerLeft = 0;
      this.headerWidth = 0;
      return;
    }

    const headerElement = this.headerFixRef?.nativeElement;
    const regionElement = this.headerRegionRef?.nativeElement;
    const listElement = this.listBodyRef?.nativeElement;

    if (!headerElement || !regionElement || !listElement) {
      return;
    }

    const regionRect = regionElement.getBoundingClientRect();
    const listRect = listElement.getBoundingClientRect();
    const headerHeight = headerElement.offsetHeight;
    const topOffset = this.getModernHeaderOffset();
    const shouldPin = regionRect.top <= topOffset && listRect.bottom > topOffset + headerHeight + 12;

    this.headerHeight = headerHeight;
    this.headerLeft = listRect.left;
    this.headerWidth = listRect.width;
    this.isHeaderPinned = shouldPin;
  }

  private getModernHeaderOffset(): number {
    const rootStyles = getComputedStyle(document.documentElement);
    const desktopOffset = parseFloat(rootStyles.getPropertyValue('--ui-modern-header-height'));
    const mobileOffset = parseFloat(rootStyles.getPropertyValue('--ui-modern-header-height-mobile'));
    const isMobile = window.matchMedia('(max-width: 720px)').matches;

    if (isMobile) {
      return Number.isFinite(mobileOffset) ? mobileOffset : 64;
    }

    return Number.isFinite(desktopOffset) ? desktopOffset : 76;
  }

  createAutomaticTransferBatches(): void {
    this.isLoadingAutomatic = true;
    // Abre o relatório já no clique, com skeleton, porque a execução roda em
    // background (Hangfire) e pode levar bastante tempo (parceiro por
    // parceiro) - evita timeout de gateway esperando 1 request só terminar.
    const dialogRef = this._dialogService.openTransferBatchMassCreateResultDialog(null);

    this._apiLotsService.triggerAutomaticTransferBatches().subscribe({
      next: (response) => {
        this.isLoadingAutomatic = false;
        const execution = response?.Data;

        if (!execution) {
          dialogRef.close();
          this._snackbarService.openSnackBar(
            response?.Message || 'Não foi possível iniciar a geração de lotes.',
            'danger',
            5000
          );
          return;
        }

        this.pollAutomaticBatchExecution(execution.ExecutionId, dialogRef);
      },
      error: (error) => {
        this.isLoadingAutomatic = false;
        dialogRef.close();
        const message = error?.error?.Message || 'Erro ao gerar lotes automáticos. Tente novamente.';
        this._snackbarService.openSnackBar(message, 'danger', 5000);
      }
    });
  }

  // Consulta o andamento da execução a cada 3s até ela terminar (Completed/
  // Failed). Fechar o modal só para de acompanhar por aqui - o job continua
  // rodando no servidor normalmente.
  private pollAutomaticBatchExecution(
    executionId: string,
    dialogRef: MatDialogRef<ModalTransferBatchMassCreateResultComponent>
  ): void {
    this.automaticExecutionSubscription?.unsubscribe();

    this.automaticExecutionSubscription = timer(0, 3000)
      .pipe(
        switchMap(() => this._apiLotsService.getAutomaticTransferBatchExecution(executionId)),
        takeWhile((response) => {
          const status = response?.Data?.Status;
          return status === 'Queued' || status === 'Running';
        }, true),
        takeUntil(dialogRef.afterClosed())
      )
      .subscribe({
        next: (response) => {
          const execution = response?.Data;

          if (!execution) {
            return;
          }

          const executionInfo = this.toExecutionInfo(execution);

          if (execution.Status === 'Completed') {
            this.searchDateField();
            this.handleAfterProcessBatchs(execution.Result, dialogRef, executionInfo);
          } else if (execution.Status === 'Failed') {
            dialogRef.componentInstance.setError(execution.ErrorMessage, executionInfo);
            this.checkedBatches = [];
            this.exceptionsBatches = [];
            this.checkAll = false;
            this.resetPagination();
          } else {
            dialogRef.componentInstance.setRunning(execution.ProcessedCount, execution.TotalCount, executionInfo, {
              TotalContracts: execution.TotalContracts ?? 0,
              ProcessedContracts: execution.ProcessedContracts ?? 0,
              CurrentBeneficiaryName: execution.CurrentBeneficiaryName,
              CurrentContractCount: execution.CurrentContractCount ?? 0
            });
          }
        },
        error: () => {
          dialogRef.componentInstance.setError('Não foi possível consultar o andamento da execução.');
        }
      });
  }

  sendTransferBatchEmail(batches: ISendBatchEmail): void {
    const batchIds$ = batches.BatchIds.map((batchId) => this._apiLotsService.sendTransferBatchEmail(batchId));

    forkJoin(batchIds$).subscribe({
      next: (response) => {
        this.handleAfterSendEmail(response);
        this.getBatches();
      }
    });
  }

  sendTransferEstimatedPaymentBatchEmail(batches: ISendBatchEmail): void {
    const batchIds$ = batches.BatchIds.map((batchId) => this._apiLotsService.sendTransferEstimatedPaymentBatchEmail(batchId));

    forkJoin(batchIds$).subscribe({
      next: (response) => {
        this.handleAfterSendEmail(response);
        this.getBatches();
      }
    });
  }

  handleAfterProcessBatchs(
    result: ITransferBatchMassCreateResult | undefined,
    dialogRef: MatDialogRef<ModalTransferBatchMassCreateResultComponent>,
    executionInfo?: IMassCreateExecutionInfo
  ): void {
    if (result) {
      dialogRef.componentInstance.setResult(result, executionInfo);
    }
    else {
      dialogRef.close();
      this._snackbarService.openSnackBar('Execução finalizada.', 'warning');
    }

    this.checkedBatches = [];
    this.exceptionsBatches = [];
    this.checkAll = false;
    this.resetPagination();
  }

  handleAfterSendEmail(response: any): void {
    let allBatchsSent = true;

    if (Array.isArray(response) && response.length > 0)
      allBatchsSent = response.every((sent: any) => sent == null || (sent != null && sent.allBatchsSent == true || sent.allBatchsSent == null));
    else
      allBatchsSent = response.allBatchsSent;

    if (allBatchsSent == true || allBatchsSent == null)
      this._snackbarService.openSnackBar('Disparo de e-mail realizado com sucesso!', 'success');
    else
      this._snackbarService.openSnackBar('Alguns lotes não foram enviados!', 'warning');

    this.checkedBatches = [];
    this.exceptionsBatches = [];
    this.checkAll = false;
    this.resetPagination();
  }

  resetPagination(): void {
    this.skip = 0;
  }

  orderChange(fields: IFieldOrder[]): void {
    this.fieldsOrder = [...fields];
    this.getBatches(false, { Skip: 0, Take: this.skip + this.take });
  }

  resetDateValue(): void {
    this.batchDateFrom.setValue('');
    this.batchDateTo.setValue('');
    this.contractInclusionDateFrom.setValue('');
    this.contractInclusionDateTo.setValue('');
    this.searchDateField();
  }

  getStorageFilter(): void {
    const bagSalva = localStorage.getItem('bag_t');
    const bag = JSON.parse(bagSalva || '{}');

    this.batchDateFrom.setValue(bag.dataIni);
    this.batchDateTo.setValue(bag.dataFim);
    this.contractInclusionDateFrom.setValue(bag.dataIniAlteracao || '');
    this.contractInclusionDateTo.setValue(bag.dataFimAlteracao || '');
  }
}
